import { type VariantProp, type VariantProps, cv } from "@suse-o11y/class-variance";
import type { JSON } from "@suse-o11y/json";
import { type Signal, batch, effect, useComputed } from "@suse-o11y/signal";
import { Slot, type Slottable } from "@suse-o11y/slot";
import { toJSON } from "@suse-o11y/to-json";
import React from "react";

import { Collapsible } from "~/components/collapsible";

import { Wrap } from "../conditional-wrapper/Wrap";

import styles from "./widget.module.css";
import { WidgetContextProvider, useWidgetExpandContext } from "./widget-context";

const variants = cv(styles.root, {
	variants: {
		/**
		 * Whether to render the widget without paddings.
		 */
		card: {
			true: styles.rootCard,
		},

		/**
		 * Draws a border between header and body when expanded
		 */
		divided: {
			true: styles.rootDivided,
		},

		compact: {
			true: styles.rootCompact,
		},

		/**
		 * Whether to render the widget without paddings.
		 */
		frameless: {
			true: styles.rootFrameless,
		},

		/**
		 * Whether to render the widget with hover/focus/disabled/etc styles.
		 */
		interactive: {
			true: styles.rootInteractive,
		},

		/**
		 * Vertical padding for widget.
		 */
		py: {
			sm: styles.rootPySm,
			md: styles.rootPyMd,
		},

		/**
		 * Horizontal padding for widget.
		 */
		px: {
			md: styles.rootPxMd,
			xl: styles.rootPxXl,
		},
	},

	defaultVariants: {
		py: "md",
		px: "md",
	},
});

export type WidgetVerticalPadding = VariantProp<typeof variants, "py">;

export type WidgetHorizontalPadding = VariantProp<typeof variants, "px">;

export interface WidgetRegularProps
	extends Slottable,
		Omit<VariantProps<typeof variants>, "compact">,
		React.HTMLAttributes<HTMLElement> {
	role?: React.AriaRole;

	className?: string;

	tabIndex?: number;

	/**
	 * A signal to control the expansion state of the widget.
	 * The widget renders the expand/collapse button if this is provided.
	 */
	isExpanded?: Signal<boolean>;

	children: React.ReactNode;

	compact?: false;

	namespace?: never;

	isExpandedByDefault?: never;
}

export interface WidgetNamespacedProps
	extends Omit<WidgetRegularProps, "isExpanded" | "namespace" | "isExpandedByDefault"> {
	/**
	 * When defined, a widget keeps the expansion state in the global context.
	 * The global state is assigned if `isExpanded` carries `undefined`.
	 * The namespace can be any primitive JSON value - it is converted to a string.
	 */
	namespace: Exclude<JSON, null>;

	/**
	 * When undefined it inherits the value from the global context.
	 */
	isExpanded?: Signal<undefined | boolean>;

	/**
	 * Assigned when both `isExpanded` and the global state are undefined.
	 *
	 * @default false
	 */
	isExpandedByDefault?: boolean;
}

export interface WidgetCompactProps extends Omit<WidgetRegularProps, "isExpanded" | "compact"> {
	/**
	 * Whether to render the widget in a compact mode,
	 * meaning the title is grayed out and there is no gap between header and body.
	 */
	compact: true;

	isExpanded?: never;
}

export type WidgetProps = WidgetRegularProps | WidgetNamespacedProps | WidgetCompactProps;

export const Widget = React.forwardRef<HTMLDivElement, WidgetProps>(
	({ namespace, isExpanded, isExpandedByDefault = false, ...props }, ref) => {
		if (namespace == null) {
			return (
				<WidgetWithoutNamespace isExpanded={isExpanded}>
					<WidgetRoot expandable={isExpanded != null} ref={ref} {...props} />
				</WidgetWithoutNamespace>
			);
		}

		return (
			<WidgetWithNamespace isExpanded={isExpanded} isExpandedByDefault={isExpandedByDefault} namespace={namespace}>
				<WidgetRoot expandable ref={ref} {...props} />
			</WidgetWithNamespace>
		);
	},
);

interface WidgetRootProps extends Omit<WidgetProps, "isExpanded" | "namespace" | "isExpandedByDefault"> {
	expandable: boolean;
}

const WidgetRoot = React.forwardRef<HTMLDivElement, WidgetRootProps>(
	({ asChild, className, expandable, compact, card, frameless, divided, interactive, py, px, ...props }, ref) => {
		const Component = asChild ? Slot : "div";

		return (
			<WidgetContextProvider value={expandable}>
				<Component
					className={variants({ className, card, frameless, divided, compact, interactive, py, px })}
					ref={ref}
					tabIndex={interactive ? 0 : undefined}
					{...props}
				/>
			</WidgetContextProvider>
		);
	},
);

const WidgetWithoutNamespace: React.FC<
	React.PropsWithChildren<{
		isExpanded?: Signal<boolean>;
	}>
> = ({ isExpanded, children }) => {
	const expandable = isExpanded != null;
	const open = useComputed((monitor) => isExpanded?.read(monitor), [isExpanded]);

	return (
		<Wrap
			if={expandable}
			with={<Collapsible asChild onOpenChange={(expanded) => isExpanded?.write(expanded)} open={open} />}
		>
			{children}
		</Wrap>
	);
};

const WidgetWithNamespace: React.FC<
	React.PropsWithChildren<{
		isExpanded?: Signal<undefined | boolean>;
		isExpandedByDefault: boolean;
		namespace: JSON;
	}>
> = ({ isExpanded: isExpandedProp, isExpandedByDefault, namespace: json, children }) => {
	const namespace = toJSON(json);

	const isExpandedMap = useWidgetExpandContext();

	const open = useComputed(
		(monitor) => isExpandedProp?.read(monitor) ?? isExpandedMap.read(monitor).get(namespace) ?? isExpandedByDefault,
		[isExpandedProp, isExpandedMap, namespace, isExpandedByDefault],
	);

	React.useEffect(
		() =>
			effect((monitor) => {
				const isExpandedByUser = isExpandedMap.read(monitor).get(namespace);

				isExpandedProp?.write((expanded = isExpandedByUser) => expanded);
			}),
		[isExpandedProp, isExpandedMap, namespace],
	);

	return (
		<Collapsible
			asChild
			onOpenChange={(expended) => {
				batch(() => {
					isExpandedProp?.write(expended);
					isExpandedMap.write((current) => new Map(current).set(namespace, expended));
				});
			}}
			open={open}
		>
			{children}
		</Collapsible>
	);
};
